/**
 * One alcove on a primary edge. Paint order is load-bearing (it doubles as
 * hit-test priority): body polygon, corner-hosted segments, the
 * selected-state outline, wall highlights, and corner handles, then the
 * always-on-top wall hit strips, gaps, and wall-hosted segments, and last
 * the delete icon.
 */

import {
  ALCOVE_EDGE_NAMES,
  alcoveCornerLineFrame,
  alcoveWallLineFrame,
  EdgeName,
  FloorPlanEdgeSegment,
  FloorPlanRoom,
  OUTER_WHICH,
  Point,
  worldOnLine,
} from '@kaiizen/kai-floor-plan';
import {
  chamferData,
  diagCursor,
  edgeSegmentPath,
  EditorMutableState,
  isLineSegmentSelected,
  lineGaps,
  perpCursor,
  svgPoints,
} from '@kaiizen/kai-floor-plan-editor-core';
import { Fragment } from 'react';
import { ChamferCornerHandle, EdgeSel, TrashIcon, WallHitStrip } from './handles';
import { ALCOVE_BODY_STYLE } from './planCss';
import { usePlanExport } from './planExportContext';
import { SegmentGlyph } from './SegmentGlyph';

export interface AlcoveItemProps {
  st: EditorMutableState;
  s: FloorPlanRoom;
  edge: EdgeName;
  index: number;
  al: FloorPlanEdgeSegment;
  /** Slide-along-the-edge cursor of the host primary edge. */
  sideCur: string;
  sp: (p: Point) => Point;
  scale: number;
}

export function AlcoveItem({ st, s, edge, index: i, al, sideCur, sp, scale }: AlcoveItemProps): React.JSX.Element {
  const exporting = usePlanExport();
  const selHere = st.selection;
  const alcoveActive = (selHere.kind === 'alcove' || selHere.kind === 'alcoveCorner') && selHere.edge === edge && selHere.index === i;
  // Corner controls show while the alcove itself, either of its corners, or
  // any of its three walls is selected — without the wall selection also
  // triggering the whole-alcove outline highlight.
  const cornerControlsActive = alcoveActive
    || (selHere.kind === 'alcoveWall' && selHere.edge === edge && selHere.index === i);
  const depth = al.depth ?? 0;
  // Body shape AND selection outline share ONE path: edgeSegmentPath (the
  // real walls — chamfers included). As a <polygon> it auto-closes
  // across the mouth (the filled/hittable alcove area); as a <polyline> the
  // mouth (opening into the room) is omitted.
  const wallPtsArr = edgeSegmentPath(s, edge, i).map(sp);
  const wallPts = svgPoints(wallPtsArr);
  return (
    <>
      {!exporting && <polygon css={ALCOVE_BODY_STYLE} data-handle={`alcove:${edge}:${i}`} points={wallPts} style={{ cursor: sideCur }} />}
      {
        /* Corner-hosted segments (a window on a chamfer diagonal) render + hit like
          every other segment, regardless of whether the alcove is selected. */
      }
      {OUTER_WHICH.map(which => {
        const clf = alcoveCornerLineFrame(s, edge, i, which);
        if (!clf) return null;
        const wfn = (ss: number, o: number) => worldOnLine(clf, ss, o);
        return (
          <Fragment key={which}>
            {(al[which]?.segments ?? []).map((seg, csi) => {
              const segSel = isLineSegmentSelected(st.selection, { k: 'ac', edge, storageIndex: i, which }, csi);
              return (
                <SegmentGlyph
                  key={csi}
                  wfn={wfn}
                  perp={clf.perp}
                  seg={seg}
                  bodyHandle={`ls:ac:${edge}:${i}:${which}:${csi}`}
                  startHandle={`ls-s:ac:${edge}:${i}:${which}:${csi}`}
                  endHandle={`ls-e:ac:${edge}:${i}:${which}:${csi}`}
                  delHandle={`ls-d:ac:${edge}:${i}:${which}:${csi}`}
                  active={segSel}
                  sp={sp}
                  scale={scale}
                />
              );
            })}
          </Fragment>
        );
      })}
      {alcoveActive && (
        <>
          <EdgeSel points={wallPtsArr} />
          {
            /* Wall-handle affordance: highlight the three ring-derived walls. The
              HITTING is owned by the always-present alcovewall strips (rendered
              later, on top) — one drag path per wall, visual accent here only. */
          }
          {ALCOVE_EDGE_NAMES.map(wall => {
            const wlf = alcoveWallLineFrame(s, edge, i, wall);
            if (!wlf || wlf.length <= 0.05) return null;
            const wA = sp(wlf.start), wB = sp(wlf.end);
            return <EdgeSel key={wall} points={[wA, wB]} />;
          })}
        </>
      )}
      {cornerControlsActive && OUTER_WHICH.map(which => {
        const chamfer = chamferData(st, { kind: 'alcoveCorner', edge, index: i, which })!;
        return (
          <ChamferCornerHandle
            key={which}
            chamfer={chamfer}
            project={sp}
            handle={`alcove:${edge}:${i}:${which}`}
            selected={selHere.kind === 'alcoveCorner' && selHere.edge === edge && selHere.index === i && selHere.which === which}
            cursor={diagCursor(chamfer.sx, chamfer.sy)}
            diagonalFrame={alcoveCornerLineFrame(s, edge, i, which)}
          />
        );
      })}
      {depth > 0.01 && ALCOVE_EDGE_NAMES.map(wall => {
        const lf = alcoveWallLineFrame(s, edge, i, wall);
        if (!lf || lf.length < 0.05) return null;
        const wallSel = selHere.kind === 'alcoveWall' && selHere.edge === edge && selHere.index === i && selHere.wall === wall;
        const wA = sp(lf.start), wB = sp(lf.end);
        const wallEffPerp = wall === 'leftEdge' ? { x: -lf.perp.x, y: -lf.perp.y } : lf.perp;
        const wallCur = perpCursor(wallEffPerp);
        const wallEdge = al[wall];
        const wfn = (ss: number, o: number) => worldOnLine(lf, ss, o);
        return (
          <Fragment key={wall}>
            {wallSel && <EdgeSel points={[wA, wB]} />}
            {!exporting && <WallHitStrip a={wA} b={wB} perp={lf.perp} scale={scale} handle={`alcovewall:${edge}:${i}:${wall}`} cursor={wallCur} />}
            {wallEdge && (
              <>
                {!exporting && lineGaps(wallEdge.segments, 0, lf.length).map(gap => {
                  const gA = sp(worldOnLine(lf, gap.gs, 0)), gB = sp(worldOnLine(lf, gap.ge, 0));
                  return (
                    <WallHitStrip
                      key={`${gap.gs}:${gap.ge}`}
                      a={gA}
                      b={gB}
                      perp={lf.perp}
                      scale={scale}
                      handle={`awgap:${edge}:${i}:${wall}:${gap.gs}:${gap.ge}`}
                      cursor={wallCur}
                    />
                  );
                })}
                {wallEdge.segments.map((seg, wsi) => {
                  const segSel = isLineSegmentSelected(selHere, { k: 'aw', edge, storageIndex: i, wall }, wsi);
                  return (
                    <SegmentGlyph
                      key={wsi}
                      wfn={wfn}
                      perp={lf.perp}
                      seg={seg}
                      bodyHandle={`ls:aw:${edge}:${i}:${wall}:${wsi}`}
                      startHandle={`ls-s:aw:${edge}:${i}:${wall}:${wsi}`}
                      endHandle={`ls-e:aw:${edge}:${i}:${wall}:${wsi}`}
                      delHandle={`ls-d:aw:${edge}:${i}:${wall}:${wsi}`}
                      active={segSel}
                      sp={sp}
                      scale={scale}
                    />
                  );
                })}
              </>
            )}
          </Fragment>
        );
      })}
      {alcoveActive && (() => {
        // Delete icon dead-center in the alcove while it's selected — same
        // affordance as free elements. Rendered last so it wins the hit test.
        const xValues = wallPtsArr.map(point => point.x);
        const yValues = wallPtsArr.map(point => point.y);
        const center = {
          x: (Math.min(...xValues) + Math.max(...xValues)) / 2,
          y: (Math.min(...yValues) + Math.max(...yValues)) / 2,
        };
        return <TrashIcon p={center} handle={`delalcove:${edge}:${i}`} />;
      })()}
    </>
  );
}
